/**
 * Typed wrappers for every backend endpoint the live system actually calls.
 *
 * Grouped by the backend router that serves them. Each function maps 1:1 onto a
 * route in Backend/routes/*.js — nothing here is speculative, and nothing the
 * live frontend used is missing.
 */

import { request, requestFresh, qs, uploadBinary, fetchBlob, postBlob } from './client';

/* ========================================================================== */
/* auth — Backend/routes/auth.js                                              */
/* ========================================================================== */

export const authApi = {
  /**
   * POST /api/auth/login -> { token, user, signedInWith, languages, country, consentPending }
   *
   * `identifier` is a username, an email or a mobile number — which of them
   * work is the account's `signInMethods` policy (المرحلة 1.1).
   */
  login: (identifier, password) =>
    request('/auth/login', { method: 'POST', body: { identifier, password }, auth: false }),

  /** GET /api/auth/policy — the sign-in screens' rules, before anyone is signed in. */
  policy: () => requestFresh('/auth/policy', { auth: false }),

  /** POST /api/auth/forgot-password { identifier, channel } — the same answer whether or not the account exists. */
  forgotPassword: (identifier, channel) =>
    request('/auth/forgot-password', { method: 'POST', body: { identifier, channel }, auth: false }),

  /** POST /api/auth/reset-password { identifier, code, newPassword } | { token, newPassword } */
  resetPassword: (payload) => request('/auth/reset-password', { method: 'POST', body: payload, auth: false }),

  /** GET /api/auth/me — «بياناتي». */
  me: () => requestFresh('/auth/me'),

  /** POST /api/auth/contact/start { channel: 'email'|'phone', value } — a code to the new contact. */
  contactStart: (channel, value) => request('/auth/contact/start', { method: 'POST', body: { channel, value } }),

  /** POST /api/auth/contact/confirm { channel, code } */
  contactConfirm: (channel, code) => request('/auth/contact/confirm', { method: 'POST', body: { channel, code } }),

  /** GET /api/auth/sessions -> { sessions: [{ id, device, ip, method, lastSeenAt, current }] } */
  sessions: () => requestFresh('/auth/sessions'),

  /** DELETE /api/auth/sessions/:id — sign one device out. */
  endSession: (id) => request(`/auth/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** POST /api/auth/sessions/revoke-all { keepCurrent } — «خروج من كل الأجهزة». */
  endAllSessions: (keepCurrent = false) => request('/auth/sessions/revoke-all', { method: 'POST', body: { keepCurrent } }),

  /** POST /api/auth/logout — this device's session ends on the server too. */
  logout: () => request('/auth/logout', { method: 'POST' }),

  /** GET /api/auth/consent -> { required, texts, accepted, pending } */
  consent: () => requestFresh('/auth/consent'),

  /** POST /api/auth/consent/accept { versionIds, via } */
  acceptConsent: (versionIds, via = 'login') => request('/auth/consent/accept', { method: 'POST', body: { versionIds, via } }),

  /**
   * POST /api/auth/register — self-registration. The backend only allows the
   * roles "patient" and "doctor"; doctors are created with status "pending".
   */
  register: (payload) => request('/auth/register', { method: 'POST', body: payload, auth: false }),

  /** POST /api/auth/google-verify -> { token, user } | { status: 'requires_role', googleData } */
  googleVerify: (token) =>
    request('/auth/google-verify', { method: 'POST', body: { token }, auth: false }),

  /** POST /api/auth/google-complete -> { token, user } | { status: 'pending' } */
  googleComplete: (googleData, role) =>
    request('/auth/google-complete', { method: 'POST', body: { ...googleData, role }, auth: false }),

  /** PUT /api/auth/profile — updates the avatar ("picture") and/or "ringtone". */
  updateProfile: (payload) => request('/auth/profile', { method: 'PUT', body: payload }),

  /** GET /api/auth/ringtone -> { ringtone } — the dose alarm's uploaded tone, or null. */
  ringtone: () => requestFresh('/auth/ringtone'),

  /**
   * PUT /api/auth/password { currentPassword, newPassword } — change your own.
   * Clears the must-change flag a hospital admin's reset sets.
   */
  changePassword: (currentPassword, newPassword) =>
    request('/auth/password', { method: 'PUT', body: { currentPassword, newPassword } }),

  /**
   * GET /api/auth/capabilities
   *   -> { id, role, permissions[], institutionId, memberRole, servesDoctorId }
   *
   * What this session may do, decided by the server. Fetched rather than
   * derived so the role->capability table exists in exactly one place; see
   * domain/rbac.js for why the browser deliberately does not compute this.
   *
   * requestFresh, not request: a hospital admin who has just narrowed a
   * co-admin's privileges expects the next reload to reflect it, and a cached
   * capability list is a permission that appears not to have been revoked.
   */
  capabilities: () => requestFresh('/auth/capabilities'),
};

/* ========================================================================== */
/* categories — Backend/routes/categories.js                                  */
/* ========================================================================== */

export const categoryApi = {
  /** GET /api/categories -> [{ id, name, icon, count }] (count = active doctors) */
  list: () => request('/categories', { auth: false }),

  /** POST /api/categories (the Super Admin, from المرحلة 1.3) */
  create: (payload) => request('/categories', { method: 'POST', body: payload }),

  /**
   * المرحلة 1.3. `institutionId` reads that institution's names, order and
   * visibility over the platform's; `all` keeps the hidden ones (the Super
   * Admin, or a manager reading its own view).
   */
  listFor: ({ institutionId, all } = {}) =>
    request(`/categories${qs({ institutionId, all: all ? 1 : undefined })}`, { auth: Boolean(all) }),

  /** PUT /api/categories/:id { name, nameEn, icon, descAr, descEn, sortOrder, visibleToPatient, isFamilyMedicine } */
  update: (id, payload) => request(`/categories/${encodeURIComponent(id)}`, { method: 'PUT', body: payload }),

  /** PUT /api/categories/order { ids } */
  reorder: (ids) => request('/categories/order', { method: 'PUT', body: { ids } }),

  /** PUT /api/categories/institution/:categoryId { name, nameEn, sortOrder, visible } — a manager, for its own institution */
  updateForInstitution: (categoryId, payload) =>
    request(`/categories/institution/${encodeURIComponent(categoryId)}`, { method: 'PUT', body: payload }),
};

/* ========================================================================== */
/* doctors — Backend/routes/doctors.js                                        */
/* ========================================================================== */

export const doctorApi = {
  /** GET /api/doctors — public list of active doctors with DoctorProfile + Clinics */
  list: () => request('/doctors', { auth: false }),

  /**
   * المرحلة 1.3 — the list as a patient searches it: `sort` (soonest, rating,
   * price), `mode`, `categoryId`, `q`, `institutionId`. Each row carries
   * `discovery: { soonest, price, ratingAvg, ratingCount, modes, … }`.
   */
  discover: (params = {}) => request(`/doctors${qs({ sort: 'none', ...params })}`),

  /** GET /api/doctors/:id/public — the doctor's page */
  publicProfile: (id) => request(`/doctors/${id}/public`),

  /** GET /api/doctors/:id/reviews */
  reviews: (id) => request(`/doctors/${id}/reviews`),

  /** POST /api/doctors/:id/reviews { bookingId, rating, comment } */
  review: (id, payload) => request(`/doctors/${id}/reviews`, { method: 'POST', body: payload }),

  /** GET /api/doctors/profile-reviews — profiles waiting for their one review */
  profileReviews: () => requestFresh('/doctors/profile-reviews'),

  /** GET /api/doctors/reviews/pending — the moderation list */
  pendingReviews: () => requestFresh('/doctors/reviews/pending'),

  /** PUT /api/doctors/:id/reviews/:reviewId { status: published|hidden } */
  moderateReview: (doctorId, reviewId, status) =>
    request(`/doctors/${doctorId}/reviews/${reviewId}`, { method: 'PUT', body: { status } }),

  /** PUT /api/doctors/:id/profile-review { decision: publish|return, reason } */
  reviewProfile: (id, decision, reason) =>
    request(`/doctors/${id}/profile-review`, { method: 'PUT', body: { decision, reason } }),

  /** GET /api/doctors/profile — the logged-in doctor, with profile/clinics/offers */
  profile: () => request('/doctors/profile'),

  /** PUT /api/doctors/profile — prices, discounts, branding, print styling */
  updateProfile: (payload) => request('/doctors/profile', { method: 'PUT', body: payload }),

  /** GET /api/doctors/dashboard -> { todayOverview, comparativeStats, dailyPerformance } */
  dashboard: () => request('/doctors/dashboard'),

  /** GET /api/doctors/packages — marketing packages catalogue */
  packages: () => request('/doctors/packages', { auth: false }),

  /** GET /api/doctors/package-requests — the doctor's own requests */
  packageRequests: () => request('/doctors/package-requests'),

  /** POST /api/doctors/package-requests */
  requestPackage: (packageId, price) =>
    request('/doctors/package-requests', { method: 'POST', body: { packageId, price } }),

  /** PATCH /api/doctors/package-requests/:id/topics — merged into contentDetails */
  updatePackageTopics: (id, contentDetails) =>
    request(`/doctors/package-requests/${id}/topics`, {
      method: 'PATCH',
      body: { contentDetails },
    }),

  /** POST /api/doctors/clinics */
  createClinic: (payload) => request('/doctors/clinics', { method: 'POST', body: payload }),

  /** PUT /api/doctors/clinics/:id */
  updateClinic: (id, payload) =>
    request(`/doctors/clinics/${id}`, { method: 'PUT', body: payload }),

  /* --- dated offers ------------------------------------------------------ */

  /** GET /api/doctors/offers — the signed-in doctor's own offers */
  offers: () => request('/doctors/offers'),

  /** POST /api/doctors/offers -> the created offer */
  createOffer: (payload) => request('/doctors/offers', { method: 'POST', body: payload }),

  /** PUT /api/doctors/offers/:id */
  updateOffer: (id, payload) =>
    request(`/doctors/offers/${id}`, { method: 'PUT', body: payload }),

  /** DELETE /api/doctors/offers/:id */
  deleteOffer: (id) => request(`/doctors/offers/${id}`, { method: 'DELETE' }),

  /* --- day-level schedule overrides -------------------------------------- */

  /** GET /api/doctors/extra-hours?doctorId= — public, so patients see them too */
  extraHours: (doctorId) => request(`/doctors/extra-hours${qs({ doctorId })}`, { auth: false }),

  /**
   * PUT /api/doctors/extra-hours — add or replace one day's evening block.
   * Sending an empty `slots` array removes the block for that clinic and date.
   */
  saveExtraHours: (payload) =>
    request('/doctors/extra-hours', { method: 'PUT', body: payload }),

  /** DELETE /api/doctors/extra-hours/:id */
  deleteExtraHours: (id) => request(`/doctors/extra-hours/${id}`, { method: 'DELETE' }),

  /** GET /api/doctors/cancelled-days?doctorId= — public */
  cancelledDays: (doctorId) =>
    request(`/doctors/cancelled-days${qs({ doctorId })}`, { auth: false }),

  /**
   * POST /api/doctors/cancelled-days — close a day.
   * The server cancels that day's bookings in the same transaction and reports
   * how many in `cancelledBookings`.
   */
  cancelDay: (date, reason) =>
    request('/doctors/cancelled-days', { method: 'POST', body: { date, reason } }),

  /** DELETE /api/doctors/cancelled-days/:date — reopen. Bookings are not restored. */
  reopenDay: (date) => request(`/doctors/cancelled-days/${date}`, { method: 'DELETE' }),
};

/* ========================================================================== */
/* clinics — Backend/routes/clinics.js                                        */
/* ========================================================================== */

export const clinicApi = {
  /** GET /api/clinics?doctorId= — clinics with their extra_services */
  listByDoctor: (doctorId) => request(`/clinics${qs({ doctorId })}`, { auth: false }),

  /** GET /api/clinics/patient/:id — a single clinic, patient-facing */
  getForPatient: (clinicId) => request(`/clinics/patient/${clinicId}`, { auth: false }),

  /** POST /api/clinics — body must carry doctor_id, may carry extra_services */
  create: (payload) => request('/clinics', { method: 'POST', body: payload }),

  /** PUT /api/clinics/:id — replaces extra_services and cascades to the service group */
  update: (id, payload) => request(`/clinics/${id}`, { method: 'PUT', body: payload }),

  /** POST /api/clinics/:targetId/link-services/:sourceId — join a service group */
  linkServices: (targetId, sourceId) =>
    request(`/clinics/${targetId}/link-services/${sourceId}`, { method: 'POST' }),

  /** POST /api/clinics/:id/unlink-services — leave the service group */
  unlinkServices: (id) => request(`/clinics/${id}/unlink-services`, { method: 'POST' }),
};

/* ========================================================================== */
/* bookings — Backend/routes/bookings.js                                      */
/* ========================================================================== */

export const bookingApi = {
  /**
   * GET /api/bookings — supports doctorId / patientId / clinicId / status / date.
   * Signed in: a booking comes whole to its doctor, its patient, the admin and
   * the hospital staff who work it; anyone else gets the slot alone (clinicId,
   * date, time, status), which is all a patient's calendar needs.
   */
  list: (filters = {}) => request(`/bookings${qs(filters)}`),

  /** Cache-busted variant used by the polling layer. */
  listFresh: (filters = {}) => requestFresh(`/bookings${qs(filters)}`),

  /**
   * POST /api/bookings/create — transactional. It row-locks the clinic and
   * rejects with 409 when maxBookingsPerSlot is already met for that date+time.
   * The backend re-derives price and deposit from the clinic, so client price
   * fields are advisory — only finalPrice survives, to carry discounts.
   */
  create: (payload) => request('/bookings/create', { method: 'POST', body: payload }),

  /** PUT /api/bookings/:id — whitelisted field update */
  update: (id, payload) => request(`/bookings/${id}`, { method: 'PUT', body: payload }),

  /** PUT /api/bookings/:id/refund — the clinic returns a cancelled booking's deposit, with the transfer's receipt. */
  settleRefund: (id, refundReceipt) =>
    request(`/bookings/${id}/refund`, { method: 'PUT', body: { refundReceipt } }),

  /** PUT /api/bookings/:id/status */
  updateStatus: (id, status) =>
    request(`/bookings/${id}/status`, { method: 'PUT', body: { status } }),

  /**
   * PUT /api/bookings/:id/admin-discount — admin only.
   *
   * Send the percentage, not a price: the backend recomputes the total from
   * the stored figures so the two can never disagree. Clinic extras are held
   * out of the discount and added back afterwards.
   */
  setAdminDiscount: (id, percent) =>
    request(`/bookings/${id}/admin-discount`, { method: 'PUT', body: { percent } }),

  /** PUT /api/bookings/patient/:id/upload-receipt — base64 deposit receipt */
  uploadReceipt: (id, depositReceipt) =>
    request(`/bookings/patient/${id}/upload-receipt`, {
      method: 'PUT',
      body: { depositReceipt },
    }),

  /* المرحلة 1.3 */

  /** GET /api/bookings/match?categoryId&date&time&mode — the doctors free in that hour (403 JOURNEY_CLOSED by default) */
  match: (params) => request(`/bookings/match${qs(params)}`),

  /** GET /api/bookings/queue?scope=doctor|institution → { pending, upcoming, completed, policy } */
  queue: (scope = 'doctor') => requestFresh(`/bookings/queue${qs({ scope })}`),

  /** POST /api/bookings/:id/accept */
  accept: (id) => request(`/bookings/${id}/accept`, { method: 'POST' }),

  /** POST /api/bookings/:id/decline { reason } — the patient reads the reason */
  decline: (id, reason) => request(`/bookings/${id}/decline`, { method: 'POST', body: { reason } }),

  /** GET /api/bookings/:id/slots?date — the booking's clinic that day, the booking left out of its own count */
  slots: (id, date) => requestFresh(`/bookings/${id}/slots${qs({ date })}`),

  /** POST /api/bookings/:id/reschedule { date, time } — the patient's own booking, as patientReschedule allows */
  reschedule: (id, date, time) => request(`/bookings/${id}/reschedule`, { method: 'POST', body: { date, time } }),
};

/* ========================================================================== */
/* doctor applications — Backend/routes/doctorApplications.js (المرحلة 1.3)    */
/* ========================================================================== */

/**
 * `/mine…` takes the applicant token (a doctor waiting for approval, who has
 * no session) or the doctor's own session; the rest is the reviewers'.
 */
const asApplicant = (token) => (token ? { auth: false, headers: { Authorization: `Bearer ${token}` } } : {});

export const applicationApi = {
  mine: (token) => requestFresh('/doctor-applications/mine', asApplicant(token)),
  setCategory: (token, categoryId) =>
    request('/doctor-applications/mine', { method: 'PUT', body: { categoryId }, ...asApplicant(token) }),
  upload: (token, payload) =>
    request('/doctor-applications/mine/documents', { method: 'POST', body: payload, ...asApplicant(token) }),
  submit: (token) => request('/doctor-applications/mine/submit', { method: 'POST', ...asApplicant(token) }),
  /** The doctor's own scan, or the reviewer's. */
  image: (applicationId, docId, token) =>
    request(`/doctor-applications/${applicationId}/documents/${docId}/image`, asApplicant(token)),

  list: (status) => requestFresh(`/doctor-applications${qs({ status })}`),
  get: (id) => request(`/doctor-applications/${id}`),
  reviewDocument: (id, docId, status, reason) =>
    request(`/doctor-applications/${id}/documents/${docId}`, { method: 'PUT', body: { status, reason } }),
  approve: (id, payload) => request(`/doctor-applications/${id}/approve`, { method: 'POST', body: payload }),
  reject: (id, reason) => request(`/doctor-applications/${id}/reject`, { method: 'POST', body: { reason } }),
};

/* ========================================================================== */
/* prescriptions — Backend/routes/prescriptions.js                            */
/* ========================================================================== */

export const prescriptionApi = {
  /** GET /api/prescriptions?doctorId=&patientId= */
  list: (filters = {}) => request(`/prescriptions${qs(filters)}`, { auth: false }),

  /** GET /api/prescriptions/patient/:patientId — includes PrescribingDoctor.name */
  listForPatient: (patientId) => request(`/prescriptions/patient/${patientId}`, { auth: false }),

  /** GET /api/prescriptions/doctor/:doctorId */
  listForDoctor: (doctorId) => request(`/prescriptions/doctor/${doctorId}`),

  /** GET /api/prescriptions/booking/:bookingId — 404 when none exists yet */
  getForBooking: (bookingId) => request(`/prescriptions/booking/${bookingId}`),

  /** POST /api/prescriptions — upserts by bookingId when one is supplied */
  save: (payload) => request('/prescriptions', { method: 'POST', body: payload }),

  /** POST /api/prescriptions/upload-result — patient attaches lab/x-ray files */
  uploadResult: ({ prescriptionId, type, itemIndex, files }) =>
    request('/prescriptions/upload-result', {
      method: 'POST',
      body: { prescriptionId, type, itemIndex, files },
    }),

  /** POST /api/prescriptions/comment-result — doctor annotates a result */
  commentResult: ({ prescriptionId, type, itemIndex, doctorComment }) =>
    request('/prescriptions/comment-result', {
      method: 'POST',
      body: { prescriptionId, type, itemIndex, doctorComment },
    }),
};

/* ========================================================================== */
/* sync — Backend/routes/sync.js                                              */
/* ========================================================================== */

export const syncApi = {
  /** GET /api/sync/patients — every user holding the "patient" role */
  patients: () => request('/sync/patients'),

  /**
   * GET /api/sync/bookings — NOTE: the backend scopes this to *today only*, for
   * a single doctor. Use bookingApi.list for any historical or cross-day view.
   */
  todayBookings: (doctorId) => requestFresh(`/sync/bookings${qs({ doctorId })}`),

  /** POST /api/sync/bookings — bulk upsert of an array of bookings */
  pushBookings: (bookings) => request('/sync/bookings', { method: 'POST', body: bookings }),

  /** GET /api/sync/prescriptions — every prescription in the system */
  prescriptions: () => request('/sync/prescriptions'),

  /** POST /api/sync/prescriptions — bulk upsert */
  pushPrescriptions: (prescriptions) =>
    request('/sync/prescriptions', { method: 'POST', body: prescriptions }),

  /**
   * GET /api/sync/live-session?doctorId=
   *   -> { [doctorId]: { liveDate, liveIdx, liveBookingId, phase, rev } }
   *
   * Omit doctorId and the server resolves it: a doctor gets their own queue, a
   * secretary gets the one doctor on their membership. Passing somebody else's
   * id is refused with 403 rather than answered.
   */
  getLiveSession: (doctorId) => requestFresh(`/sync/live-session${qs({ doctorId })}`),

  /**
   * POST /api/sync/live-session — advance the live consultation queue.
   *
   * `liveBookingId` is carried alongside `liveIdx` because the index is a
   * position in a chronologically sorted day, and anything that re-sorts that
   * day — a cancellation, a walk-in, a schedule shift — would otherwise leave
   * the pointer silently aimed at a different patient. `phase` is the
   * doctor's "finished" signal, which is what the secretary's screen watches.
   */
  setLiveSession: ({ doctorId, liveDate, liveIdx, liveBookingId, phase }) =>
    request('/sync/live-session', {
      method: 'POST',
      body: { doctorId, liveDate, liveIdx, liveBookingId, phase },
    }),
};

/* ========================================================================== */
/* medical records — Backend/routes/medicalRecords.js                         */
/* ========================================================================== */

/**
 * Documents a patient brings in from outside the platform.
 *
 * The list route accepts a `patientId`, but the backend only honours it for the
 * patient themselves, an admin, or a doctor that patient has actually booked
 * with — so a doctor token is not by itself enough to read anyone's history.
 */
export const medicalRecordApi = {
  /** GET /api/medical-records?patientId= — defaults to the caller's own */
  list: (patientId) => request(`/medical-records${qs({ patientId })}`),

  /** POST /api/medical-records */
  create: (payload) => request('/medical-records', { method: 'POST', body: payload }),

  /**
   * PUT /api/medical-records/:id — omit `files` to edit the details without
   * re-uploading every attachment.
   */
  update: (id, payload) => request(`/medical-records/${id}`, { method: 'PUT', body: payload }),

  /** DELETE /api/medical-records/:id */
  remove: (id) => request(`/medical-records/${id}`, { method: 'DELETE' }),
};

/* ========================================================================== */
/* photography — Backend/routes/photography.js                                */
/* ========================================================================== */

/**
 * Promotional photography crews and their bookings.
 *
 * Availability is computed server-side because the travel buffer belongs to the
 * team and is frozen onto each booking; recomputing it in the browser would
 * drift the moment an admin changed a team's gap.
 */
export const photographyApi = {
  /** GET /api/photography/teams — active crews for a doctor, all for an admin */
  teams: () => requestFresh('/photography/teams'),

  /** POST /api/photography/teams — admin only */
  createTeam: (payload) => request('/photography/teams', { method: 'POST', body: payload }),

  /** PUT /api/photography/teams/:id — admin only */
  updateTeam: (id, payload) =>
    request(`/photography/teams/${id}`, { method: 'PUT', body: payload }),

  /** PUT /api/photography/teams/:id/portfolio — admin uploads sample work */
  setPortfolio: (id, portfolio) =>
    request(`/photography/teams/${id}/portfolio`, { method: 'PUT', body: { portfolio } }),

  /** DELETE /api/photography/teams/:id — deactivates instead when jobs exist */
  deleteTeam: (id) => request(`/photography/teams/${id}`, { method: 'DELETE' }),

  /**
   * GET /api/photography/availability — start times a shoot could begin at.
   * -> { slots: ['10:00', ...], bufferMinutes, hourlyRate, reason? }
   */
  availability: ({ teamId, date, hours }) =>
    requestFresh(`/photography/availability${qs({ teamId, date, hours })}`),

  /** GET /api/photography/bookings — scoped to the caller by the backend */
  bookings: (filters = {}) => requestFresh(`/photography/bookings${qs(filters)}`),

  /** POST /api/photography/bookings — 409 when the buffered window is taken */
  book: (payload) => request('/photography/bookings', { method: 'POST', body: payload }),

  /** PUT /api/photography/bookings/:id/status */
  setStatus: (id, status) =>
    request(`/photography/bookings/${id}/status`, { method: 'PUT', body: { status } }),
};

/* ========================================================================== */
/* google accounts — Backend/routes/googleAccounts.js                         */
/* ========================================================================== */

/**
 * Extra Google addresses that may sign in as one account.
 *
 * A doctor and their secretary share a login but not a Gmail, which the single
 * `email` column on the account could not express.
 */
export const googleAccountApi = {
  /** GET /api/google-accounts?userId= — own by default; admins may name anyone */
  list: (userId) => request(`/google-accounts${qs({ userId })}`),

  /** POST /api/google-accounts — 409 when the address is on another account */
  add: (payload) => request('/google-accounts', { method: 'POST', body: payload }),

  /** DELETE /api/google-accounts/:id */
  remove: (id) => request(`/google-accounts/${id}`, { method: 'DELETE' }),
};

/* ========================================================================== */
/* prescription templates — Backend/routes/prescriptionTemplates.js           */
/* ========================================================================== */

/**
 * A doctor’s own library of ready-made prescriptions ("الروشتات الجاهزة").
 *
 * Every route is scoped to the signed-in doctor by the backend — an id alone
 * never addresses a template, so one doctor cannot read or overwrite another’s
 * library. That matters here because a template is applied wholesale to a real
 * patient, dosing included.
 */
export const prescriptionTemplateApi = {
  /** GET /api/prescription-templates — most-used first */
  list: () => request('/prescription-templates'),

  /** POST /api/prescription-templates — 409 on a duplicate name */
  create: (payload) => request('/prescription-templates', { method: 'POST', body: payload }),

  /** PUT /api/prescription-templates/:id */
  update: (id, payload) =>
    request(`/prescription-templates/${id}`, { method: 'PUT', body: payload }),

  /**
   * POST /api/prescription-templates/:id/used — record an apply.
   * Separate from update() so applying a template cannot rewrite it.
   */
  recordUse: (id) => request(`/prescription-templates/${id}/used`, { method: 'POST' }),

  /** DELETE /api/prescription-templates/:id */
  remove: (id) => request(`/prescription-templates/${id}`, { method: 'DELETE' }),
};

/* ========================================================================== */
/* institutions — Backend/routes/institutions.js                              */
/* ========================================================================== */

/**
 * Hospitals, polyclinics and lab centres.
 *
 * Two audiences, strictly separated by the backend. The admin half creates
 * institutions and sets their quotas; the manager half runs the one
 * institution the token owns. A manager route never takes an institution id —
 * the server resolves it from the token, so there is nothing to pass that
 * would reach someone else's hospital.
 */
export const institutionApi = {
  /** GET /api/institutions/public — active institutions, for patient screens */
  publicList: (type) => request(`/institutions/public${qs({ type })}`, { auth: false }),

  /** GET /api/institutions — admin only; includes usage against quota */
  list: () => requestFresh('/institutions'),

  /** POST /api/institutions — creates the institution and its manager login */
  create: (payload) => request('/institutions', { method: 'POST', body: payload }),

  /** PUT /api/institutions/:id — admin only; quotas live here */
  update: (id, payload) => request(`/institutions/${id}`, { method: 'PUT', body: payload }),

  /** DELETE /api/institutions/:id — deactivates once it has traded */
  remove: (id) => request(`/institutions/${id}`, { method: 'DELETE' }),

  /* --- the manager's own institution ------------------------------------ */

  /** GET /api/institutions/mine */
  mine: () => requestFresh('/institutions/mine'),

  /** PUT /api/institutions/mine — presentation only; not quotas */
  updateMine: (payload) => request('/institutions/mine', { method: 'PUT', body: payload }),

  /** GET /api/institutions/mine/members */
  members: () => requestFresh('/institutions/mine/members'),

  /** POST /api/institutions/mine/members — 409 when the quota is full */
  addMember: (payload) => request('/institutions/mine/members', { method: 'POST', body: payload }),

  /** DELETE /api/institutions/mine/members/:id — suspends, never deletes */
  removeMember: (id) => request(`/institutions/mine/members/${id}`, { method: 'DELETE' }),

  /** PUT /api/institutions/mine/members/:id — edit name, username, email, phone, password… */
  updateMember: (id, payload) => request(`/institutions/mine/members/${id}`, { method: 'PUT', body: payload }),

  /**
   * POST /api/institutions/mine/members/:id/reset-password
   *   -> { member, user, temporaryPassword } — the password becomes the username
   */
  resetMemberPassword: (id) => request(`/institutions/mine/members/${id}/reset-password`, { method: 'POST' }),

  /** POST /api/institutions/mine/members/:id/reactivate — undo a suspension (quota applies) */
  reactivateMember: (id) => request(`/institutions/mine/members/${id}/reactivate`, { method: 'POST' }),

  /** GET /api/institutions/mine/finance -> { totals, byCategory, byMember, entries } */
  finance: (range = {}) => requestFresh(`/institutions/mine/finance${qs(range)}`),

  /** GET /api/institutions/mine/consultations — the doctors' bookings */
  consultations: (filters = {}) => requestFresh(`/institutions/mine/consultations${qs(filters)}`),

  /** GET /api/institutions/membership — does this account belong to one? */
  membership: () => request('/institutions/membership'),
};

/* ========================================================================== */
/* institution services — Backend/routes/institutionServices.js               */
/* ========================================================================== */

/**
 * The catalogue a patient compares, and the bookings made against it.
 *
 *  is deliberately unauthenticated: a price list behind a login is
 * not a price list. It still reads the token when one is present, so an
 * insured patient is quoted their own rate rather than a headline they will
 * never pay.
 */
export const serviceApi = {
  /** GET /api/services/catalog?categories=lab,radiology */
  catalog: (categories = []) =>
    requestFresh(`/services/catalog${qs({ categories: categories.join(',') })}`),

  /** GET /api/services/:id — priced for the caller */
  get: (id) => request(`/services/${id}`, { auth: false }),

  /** GET /api/services/:id/availability?date= -> { slots: [{ time, available }] } */
  availability: (id, date) =>
    requestFresh(`/services/${id}/availability${qs({ date })}`),

  /** POST /api/services/bookings — 409 when the slot filled while deciding */
  book: (payload) => request('/services/bookings', { method: 'POST', body: payload }),

  /** GET /api/services/bookings — scoped by the backend to who is asking */
  bookings: (filters = {}) => requestFresh(`/services/bookings${qs(filters)}`),

  /** PUT /api/services/bookings/:id/status — completing records the revenue */
  setStatus: (id, status, reason) =>
    request(`/services/bookings/${id}/status`, { method: 'PUT', body: { status, reason } }),

  /** PUT /api/services/bookings/:id/result — what reaches the medical record */
  saveResult: (id, payload) =>
    request(`/services/bookings/${id}/result`, { method: 'PUT', body: payload }),

  /* --- the manager's catalogue ------------------------------------------ */

  /** GET /api/services/mine — including inactive rows */
  mine: () => requestFresh('/services/mine'),

  /** POST /api/services/mine — 403 when the type may not sell that category */
  createMine: (payload) => request('/services/mine', { method: 'POST', body: payload }),

  /** PUT /api/services/mine/:id — the category is fixed after creation */
  updateMine: (id, payload) => request(`/services/mine/${id}`, { method: 'PUT', body: payload }),

  /** DELETE /api/services/mine/:id — hides it once it has been booked */
  removeMine: (id) => request(`/services/mine/${id}`, { method: 'DELETE' }),
};

/* ========================================================================== */
/* autocomplete — Backend/routes/autocomplete.js                              */
/* ========================================================================== */

/** The only field names the backend accepts. */
export const AUTOCOMPLETE_FIELDS = ['diagnosis', 'medicine', 'labTest', 'xray', 'notes'];

export const autocompleteApi = {
  /** GET /api/autocomplete/:doctorId -> { diagnosis: [], medicine: [], ... } */
  all: (doctorId) => request(`/autocomplete/${doctorId}`),

  /** GET /api/autocomplete/:doctorId/:field -> string[] */
  field: (doctorId, field) => request(`/autocomplete/${doctorId}/${field}`),

  /** POST /api/autocomplete/:doctorId/:field — merged, deduped case-insensitively */
  addEntries: (doctorId, field, entries) =>
    request(`/autocomplete/${doctorId}/${field}`, { method: 'POST', body: { entries } }),
};

/* ========================================================================== */
/* team — Backend/routes/team.js                                              */
/* ========================================================================== */

export const teamApi = {
  /** GET /api/team/members — profile + AssignedProjects + AssignedDoctors */
  members: () => request('/team/members'),

  /** POST /api/team/members */
  createMember: (payload) => request('/team/members', { method: 'POST', body: payload }),

  /** PUT /api/team/members/:id */
  updateMember: (id, payload) => request(`/team/members/${id}`, { method: 'PUT', body: payload }),

  /** DELETE /api/team/members/:id */
  deleteMember: (id) => request(`/team/members/${id}`, { method: 'DELETE' }),

  /** POST /api/team/assign-project */
  assignProject: (payload) => request('/team/assign-project', { method: 'POST', body: payload }),

  /** POST /api/team/assign-doctor — moderator assignment */
  assignDoctor: (payload) => request('/team/assign-doctor', { method: 'POST', body: payload }),
};

/* ========================================================================== */
/* topics — Backend/routes/topics.js                                          */
/* ========================================================================== */

export const topicApi = {
  /** GET /api/topics/package/:packageRequestId */
  listForPackage: (packageRequestId) => request(`/topics/package/${packageRequestId}`),

  /** POST /api/topics — also mirrors the title into PackageRequest.contentDetails */
  create: (payload) => request('/topics', { method: 'POST', body: payload }),

  /** PUT /api/topics/:id */
  update: (id, payload) => request(`/topics/${id}`, { method: 'PUT', body: payload }),

  /** PATCH /api/topics/:id/status */
  setStatus: (id, payload) => request(`/topics/${id}/status`, { method: 'PATCH', body: payload }),
};

/* ========================================================================== */
/* admin — Backend/routes/admin.js (every route requires role === "admin")     */
/* ========================================================================== */

/* --- the platform's two marks and name — Backend/routes/branding.js ---- */

export const brandingApi = {
  /** GET /api/branding — public: the sign-in page draws the marks too */
  get: () => request('/branding', { auth: false }),

  /** PUT /api/branding — Super Admin; any of name, agency, product (data URL or null), agencyBackdrop, productBackdrop */
  update: (payload) => request('/branding', { method: 'PUT', body: payload }),
};

export const adminApi = {
  /** GET /api/admin/doctors/pending — the registration queue */
  pendingDoctors: () => request('/admin/doctors/pending'),

  /** GET /api/admin/doctors/all — every doctor regardless of status */
  allDoctors: () => requestFresh('/admin/doctors/all'),

  /** PUT /api/admin/doctors/:id/approve */
  approveDoctor: (id) => request(`/admin/doctors/${id}/approve`, { method: 'PUT' }),

  /** PUT /api/admin/doctors/:id/suspend */
  suspendDoctor: (id) => request(`/admin/doctors/${id}/suspend`, { method: 'PUT' }),

  /** PUT /api/admin/doctors/:id/pending — used to reject a registration */
  setDoctorPending: (id) => request(`/admin/doctors/${id}/pending`, { method: 'PUT' }),

  /** GET /api/admin/packages */
  packages: () => request('/admin/packages'),

  /** POST /api/admin/packages */
  createPackage: (payload) => request('/admin/packages', { method: 'POST', body: payload }),

  /** PUT /api/admin/packages/:id */
  updatePackage: (id, payload) => request(`/admin/packages/${id}`, { method: 'PUT', body: payload }),

  /** DELETE /api/admin/packages/:id */
  deletePackage: (id) => request(`/admin/packages/${id}`, { method: 'DELETE' }),

  /** GET /api/admin/requests — every package request, with its User and Package */
  packageRequests: () => request('/admin/requests'),

  /** PUT /api/admin/requests/:id/status */
  setRequestStatus: (id, payload) =>
    request(`/admin/requests/${id}/status`, { method: 'PUT', body: payload }),

  /** PATCH /api/admin/requests/:id/content */
  setRequestContent: (id, contentDetails) =>
    request(`/admin/requests/${id}/content`, { method: 'PATCH', body: { contentDetails } }),

  /** GET /api/admin/roles */
  roles: () => request('/admin/roles'),

  /* --- AI governance --------------------------------------------------- */

  /**
   * GET /api/admin/ai-access — every doctor with their AI grant.
   *
   * Doctors with no grant row are included, carrying the defaults, so the
   * screen can grant access to someone who has never had it. Cache-busted:
   * an admin who just flipped a switch expects the row to reflect it.
   */
  aiAccess: () => requestFresh('/admin/ai-access'),

  /** PUT /api/admin/ai-access/:userId — upserts the grant */
  setAiAccess: (userId, payload) =>
    request(`/admin/ai-access/${userId}`, { method: 'PUT', body: payload }),

  /** GET /api/admin/ai-logs — the diagnostic feed, newest first */
  aiLogs: (filters = {}) => request(`/admin/ai-logs${qs(filters)}`),

  /** GET /api/admin/ai-stats — 30-day rollup: success rate, spend, per-model */
  aiStats: () => request('/admin/ai-stats'),
};

/* ========================================================================== */
/* ai — Backend/routes/ai.js                                                  */
/* ========================================================================== */

/**
 * The AI RAG engine.
 *
 * Only the non-streaming calls live here. The two streaming endpoints
 * (`/ai/analyze-medical-file` and `/ai/chat`) are deliberately absent: they are
 * driven by `features/ai/aiStream.js`, because the shared `request()` helper
 * reads the whole body before returning and would turn a token stream back
 * into one blocking response — the exact behaviour the feature exists to avoid.
 */
export const aiApi = {
  /**
   * GET /api/ai/health — is the engine configured, and how deep is the ladder?
   *
   * Unauthenticated by design: the deploy script curls it before any token
   * exists. It reports counts and model names, never a key.
   */
  health: () => request('/ai/health', { auth: false }),

  /**
   * GET /api/ai/capabilities — what may THIS user do?
   *
   * Decides whether the premium toggle renders. Not the security boundary:
   * the analyse endpoint re-derives the same answer server-side and silently
   * downgrades a request it does not believe.
   */
  capabilities: () => requestFresh('/ai/capabilities'),

  /** GET /api/ai/my-usage — the doctor's own recent runs */
  myUsage: () => request('/ai/my-usage'),
};

/* --- the front desk — Backend/routes/reception.js ---------------------- */

/**
 * The receptionist's surface.
 *
 * Every call is scoped server-side to the hospital on the caller's membership;
 * none of these paths carries an institution id, deliberately, so there is
 * nothing for a browser to edit.
 */
export const receptionApi = {
  /** GET /api/reception/fields — the hospital's custom intake fields */
  fields: () => requestFresh('/reception/fields'),

  /** POST /api/reception/fields — invent one mid-shift; idempotent on the label */
  createField: (payload) => request('/reception/fields', { method: 'POST', body: payload }),

  /** PUT /api/reception/fields/:id — rename, publish or archive */
  updateField: (id, payload) =>
    request(`/reception/fields/${id}`, { method: 'PUT', body: payload }),

  /** GET /api/reception/intake/:bookingId -> { booking, intake } */
  intake: (bookingId) => requestFresh(`/reception/intake/${bookingId}`),

  /** PUT /api/reception/intake/:bookingId — upsert; the desk does not distinguish */
  saveIntake: (bookingId, payload) =>
    request(`/reception/intake/${bookingId}`, { method: 'PUT', body: payload }),

  /** GET /api/reception/share/:bookingId -> { link } | { link: null } */
  shareLink: (bookingId) => requestFresh(`/reception/share/${bookingId}`),

  /**
   * POST /api/reception/share/:bookingId
   *   -> { token, url, expiresAt, qrSvg, whatsappUrl }
   *
   * Re-issuing revokes whatever was live for this booking, so a forwarded
   * message stops working the moment the desk reissues.
   */
  issueShare: (bookingId, ttlDays) =>
    request(`/reception/share/${bookingId}`, { method: 'POST', body: { ttlDays } }),

  /** DELETE /api/reception/share/:token — revokes, never deletes */
  revokeShare: (token) => request(`/reception/share/${token}`, { method: 'DELETE' }),
};

/* --- clinic operations — Backend/routes/operations.js ------------------ */

export const operationsApi = {
  /** GET /api/operations/board?date= — every room, doctor and booking for one day */
  board: (date) => requestFresh(`/operations/board${qs({ date })}`),

  /*
   * The clinics' day sheet (جدول العيادات): who sits where on a date, with
   * which secretary, and who sent their apologies.
   */

  /** GET /api/operations/day-schedule?date= -> { rows, summary, secretaries, mayPlan, mayExcuse } */
  daySchedule: (date) => requestFresh(`/operations/day-schedule${qs({ date })}`),

  /** PUT /api/operations/day-schedule/:clinicId { date, secretaryUserId?, startTime?, location?, note? } */
  planClinicDay: (clinicId, payload) => request(`/operations/day-schedule/${clinicId}`, { method: 'PUT', body: payload }),

  /** POST /api/operations/day-schedule/excuse { doctorId, date, reason, cancelBookings } */
  excuseDoctor: (payload) => request('/operations/day-schedule/excuse', { method: 'POST', body: payload }),

  /** DELETE /api/operations/day-schedule/excuse?doctorId=&date= */
  unexcuseDoctor: (doctorId, date) => request(`/operations/day-schedule/excuse${qs({ doctorId, date })}`, { method: 'DELETE' }),

  /** POST /api/operations/day-schedule/copy { from, to } -> { copied } */
  copyDayPlan: (from, to) => request('/operations/day-schedule/copy', { method: 'POST', body: { from, to } }),

  /**
   * POST /api/operations/schedule/delay/preview
   *
   * The same planner the apply call runs, with nothing written. The manager
   * confirms a concrete list of patients and new times, never a bare number.
   */
  previewDelay: (payload) =>
    request('/operations/schedule/delay/preview', { method: 'POST', body: payload }),

  /**
   * POST /api/operations/schedule/delay
   *
   * `shiftKey` is the idempotency token — a double-click replays the first
   * result rather than moving the day twice.
   */
  applyDelay: (payload) => request('/operations/schedule/delay', { method: 'POST', body: payload }),

  /** POST /api/operations/schedule/delay/:id/revert -> { restored, skipped } */
  revertDelay: (id) =>
    request(`/operations/schedule/delay/${id}/revert`, { method: 'POST', body: {} }),

  /** GET /api/operations/notices — the outbox, each row with a wa.me link */
  notices: (filters = {}) => requestFresh(`/operations/notices${qs(filters)}`),

  /** PUT /api/operations/notices/:id/sent — the manager confirms they sent it */
  markNoticeSent: (id) => request(`/operations/notices/${id}/sent`, { method: 'PUT', body: {} }),

  /*
   * Clinics reception's appointments — QA report §3.3. The slots come back
   * with how full each is; a write is re-checked on the server under the
   * clinic's lock, so a slot taken meanwhile answers 409 with a sentence.
   */

  /** GET /api/operations/appointments/slots?clinicId=&date=&exclude= */
  appointmentSlots: (params) => requestFresh(`/operations/appointments/slots${qs(params)}`),

  /** POST /api/operations/appointments — { clinicId, date, time, patientName, patientPhone, type, hospitalPatientId, notify } */
  createAppointment: (payload) => request('/operations/appointments', { method: 'POST', body: payload }),

  /** PUT /api/operations/appointments/:id — move it or correct the patient's details */
  updateAppointment: (id, payload) => request(`/operations/appointments/${id}`, { method: 'PUT', body: payload }),

  /** POST /api/operations/appointments/:id/cancel — { reason, notify } */
  cancelAppointment: (id, payload) => request(`/operations/appointments/${id}/cancel`, { method: 'POST', body: payload }),
};

/* --- the patient's public page — Backend/routes/publicVisit.js --------- */

/**
 * The live queue — QA report §5.3. The desks' side; the screens and the
 * patient's page read publicQueueApi below, with no identity at all.
 */
export const queueApi = {
  board: () => requestFresh('/queue/board'),
  issue: (payload) => request('/queue/tickets', { method: 'POST', body: payload }),
  ticket: (id) => requestFresh(`/queue/tickets/${id}`),
  /** action: next | call | recall | skip | done | cancel | requeue */
  move: (clinicId, action, ticketId = null) =>
    request(`/queue/clinics/${clinicId}/move`, { method: 'POST', body: { action, ...(ticketId ? { ticketId } : {}) } }),
  displays: () => requestFresh('/queue/displays'),
  createDisplay: (payload) => request('/queue/displays', { method: 'POST', body: payload }),
  updateDisplay: (id, payload) => request(`/queue/displays/${id}`, { method: 'PUT', body: payload }),
};

export const publicQueueApi = {
  display: (token) => request(`/public/queue/display/${encodeURIComponent(token)}`, { auth: false }),
  ticket: (token) => request(`/public/queue/ticket/${encodeURIComponent(token)}`, { auth: false }),
  displayStream: (token) => `/public/queue/display/${encodeURIComponent(token)}/stream`,
  ticketStream: (token) => `/public/queue/ticket/${encodeURIComponent(token)}/stream`,
};

export const publicVisitApi = {
  /**
   * GET /api/public/visit/:token
   *
   * `auth: false` is not an optimisation here, it is the contract: this page is
   * opened from a WhatsApp link by somebody with no account, and sending a
   * bearer token from a shared clinic tablet would attach the wrong identity to
   * a request that must have none.
   */
  visit: (token) => request(`/public/visit/${encodeURIComponent(token)}`, { auth: false }),

  /**
   * GET /api/public/record/:token — the patient's whole hospital record.
   * Same contract as the visit page: no account, no token, ever.
   */
  record: (token) => request(`/public/record/${encodeURIComponent(token)}`, { auth: false }),
};

/* ========================================================================== */
/* The 14-dashboard upgrade                                                   */
/* ========================================================================== */

/* --- the hospital patient file — Backend/routes/patients.js ------------ */

export const patientsApi = {
  /** GET /api/patients?q=&type= — find a returning patient by any identifier */
  search: (params = {}) => requestFresh(`/patients${qs(params)}`),
  get: (id, { card = false } = {}) => requestFresh(`/patients/${id}${card ? '?card=1' : ''}`),
  create: (payload) => request('/patients', { method: 'POST', body: payload }),
  update: (id, payload) => request(`/patients/${id}`, { method: 'PUT', body: payload }),
  byBooking: (bookingId) => requestFresh(`/patients/by-booking/${encodeURIComponent(bookingId)}`),
  linkBooking: (id, bookingId) => request(`/patients/${id}/link-booking`, { method: 'POST', body: { bookingId } }),

  coverage: (id) => requestFresh(`/patients/${id}/coverage`),
  saveCoverage: (id, payload) => request(`/patients/${id}/coverage`, { method: 'PUT', body: payload }),
  quote: (id, payload) => request(`/patients/${id}/coverage/quote`, { method: 'POST', body: payload }),

  attachments: (id) => requestFresh(`/patients/${id}/attachments`),
  attachment: (attachmentId) => request(`/patients/attachments/${attachmentId}`),
  categories: () => requestFresh('/patients/attachments/categories'),
  upload: (id, payload) => request(`/patients/${id}/attachments`, { method: 'POST', body: payload }),
  /**
   * The QA upgrade's upload: the File itself as the body, up to 50 MB, any
   * type, with progress. Text headers are percent-encoded — an Arabic file
   * name is not a valid header value as it stands.
   */
  uploadFile: (id, file, { category, title, serviceRequestId, onProgress, signal } = {}) =>
    uploadBinary(`/patients/${id}/attachments/upload`, file, {
      onProgress,
      signal,
      headers: {
        'X-File-Name': encodeURIComponent(file.name || 'file'),
        'X-File-Type': encodeURIComponent(file.type || 'application/octet-stream'),
        ...(category ? { 'X-Category': encodeURIComponent(category) } : {}),
        ...(title ? { 'X-Title': encodeURIComponent(title) } : {}),
        ...(serviceRequestId ? { 'X-Service-Request-Id': String(serviceRequestId) } : {}),
      },
    }),
  attachmentBlob: (attachmentId, options) => fetchBlob(`/patients/attachments/${attachmentId}/raw`, options),
  setAttachmentThumbnail: (attachmentId, thumbnail) =>
    request(`/patients/attachments/${attachmentId}/thumbnail`, { method: 'PUT', body: { thumbnail } }),
  removeAttachment: (attachmentId) => request(`/patients/attachments/${attachmentId}`, { method: 'DELETE' }),

  notes: (id) => requestFresh(`/patients/${id}/notes`),
  addNote: (id, payload) => request(`/patients/${id}/notes`, { method: 'POST', body: payload }),

  /** GET /api/patients/:id/file — everything the hospital holds, results included */
  file: (id) => requestFresh(`/patients/${id}/file`),

  /**
   * GET /api/patients/files?date=&to=&clinicId=&doctorId= — every file for a
   * clinic's day, for the printed set the clinics manager and the secretary
   * hand to a doctor. Returns { from, to, files, total, truncated }.
   */
  dayFiles: (params = {}) => requestFresh(`/patients/files${qs(params)}`),

  /** POST /api/patients/attachments/:id/ocr — read the picture's text again, in the background */
  rereadAttachment: (attachmentId) => request(`/patients/attachments/${attachmentId}/ocr`, { method: 'POST', body: {} }),

  portal: (id) => requestFresh(`/patients/${id}/portal`),
  issuePortal: (id, ttlDays) => request(`/patients/${id}/portal`, { method: 'POST', body: { ttlDays } }),
  revokePortal: (token) => request(`/patients/portal/${encodeURIComponent(token)}`, { method: 'DELETE' }),
};

/* --- service requests — Backend/routes/requests.js --------------------- */

export const requestsApi = {
  /** GET /api/requests?queue=contracts|accounts|desk|doctor|mine|clinics|lab|radiology|pharmacy|rooms */
  list: (params = {}) => requestFresh(`/requests${qs(params)}`),
  summary: () => requestFresh('/requests/summary'),
  get: (id) => requestFresh(`/requests/${id}`),
  create: (payload) => request('/requests', { method: 'POST', body: payload }),
  quote: (payload) => request('/requests/quote', { method: 'POST', body: payload }),
  finance: (id, payload) => request(`/requests/${id}/finance`, { method: 'POST', body: payload }),
  action: (id, payload) => request(`/requests/${id}/action`, { method: 'POST', body: payload }),
  surgery: (id, payload) => request(`/requests/${id}/surgery`, { method: 'POST', body: payload }),
  price: (id, prices) => request(`/requests/${id}/price`, { method: 'POST', body: { prices } }),
  sendToDesk: (id) => request(`/requests/${id}/send-to-desk`, { method: 'POST', body: {} }),
  pay: (id, receiptId) => request(`/requests/${id}/pay`, { method: 'POST', body: { receiptId } }),
  cancel: (id, note) => request(`/requests/${id}/cancel`, { method: 'POST', body: { note } }),
  resubmit: (id, payload) => request(`/requests/${id}`, { method: 'PUT', body: payload }),
};

/* --- the department desks ---------------------------------------------- */

export const contractsApi = {
  summary: () => requestFresh('/contracts/summary'),
  queue: (financeStatus = 'pending') => requestFresh(`/contracts/queue${qs({ financeStatus })}`),
  insured: (params = {}) => requestFresh(`/contracts/insured${qs(params)}`),
};

export const accountsApi = {
  summary: () => requestFresh('/accounts/summary'),
  queue: (stage = 'to_price') => requestFresh(`/accounts/queue${qs({ stage })}`),
  ledger: (params = {}) => requestFresh(`/accounts/ledger${qs(params)}`),
  prices: () => requestFresh('/accounts/prices'),
  /** PUT /api/accounts/prices/:id — the three numbers, never the catalogue's shape */
  updatePrice: (id, payload) => request(`/accounts/prices/${id}`, { method: 'PUT', body: payload }),
};

/** Lab, radiology and pharmacy share one shape — Backend/lib/departmentDesk.js. */
const departmentApi = (base) => ({
  summary: () => requestFresh(`/${base}/summary`),
  queue: (params = {}) => requestFresh(`/${base}/queue${qs(params)}`),
  done: (params = {}) => requestFresh(`/${base}/done${qs(params)}`),
});

export const labApi = departmentApi('lab');
export const radiologyApi = departmentApi('radiology');
export const pharmacyApi = departmentApi('pharmacy');

export const roomsApi = {
  list: (params = {}) => requestFresh(`/rooms${qs(params)}`),
  createRoom: (payload) => request('/rooms', { method: 'POST', body: payload }),
  updateRoom: (id, payload) => request(`/rooms/room/${id}`, { method: 'PUT', body: payload }),
  addBed: (payload) => request('/rooms/beds', { method: 'POST', body: payload }),
  updateBed: (id, payload) => request(`/rooms/beds/${id}`, { method: 'PUT', body: payload }),
  requests: (params = {}) => requestFresh(`/rooms/requests${qs(params)}`),
  decide: (id, payload) => request(`/rooms/requests/${id}/decision`, { method: 'POST', body: payload }),
  assign: (payload) => request('/rooms/assignments', { method: 'POST', body: payload }),
  updateAssignment: (id, payload) => request(`/rooms/assignments/${id}`, { method: 'PUT', body: payload }),
  calendar: (params = {}) => requestFresh(`/rooms/calendar${qs(params)}`),
  summary: () => requestFresh('/rooms/summary'),
};

export const nursingApi = {
  patients: () => requestFresh('/nursing/patients'),
  records: (patientId, kind) => requestFresh(`/nursing/patients/${patientId}/records${qs({ kind })}`),
  record: (payload) => request('/nursing/records', { method: 'POST', body: payload }),
  done: (id, done = true) => request(`/nursing/records/${id}/done`, { method: 'PUT', body: { done } }),
  summary: () => requestFresh('/nursing/summary'),
};

export const receiptsApi = {
  /**
   * POST /api/receipts — lines may carry { department, clinicId, doctorId,
   * examType, serviceId }; each routed line becomes that department's request,
   * cleared by the receipt. { receiptType: 'cash'|'deferred', paidAmount }.
   */
  issue: (payload) => request('/receipts', { method: 'POST', body: payload }),
  /** ?date= | ?from=&to= (a shift), &issuedBy=me|<id>, &hospitalPatientId=, &status= */
  list: (params = {}) => requestFresh(`/receipts${qs(params)}`),
  get: (id) => requestFresh(`/receipts/${id}`),
  collect: (id) => request(`/receipts/${id}/collect`, { method: 'POST', body: {} }),
  void: (id) => request(`/receipts/${id}/void`, { method: 'POST', body: {} }),
  /** «مرتجع» on a voucher: { reason, lines: [{ index, quantity? }] } -> 201 { receipt, refund, full } */
  refund: (id, payload) => request(`/receipts/${id}/refund`, { method: 'POST', body: payload }),
  /** GET /api/receipts/options -> { doctors[{clinics}], services, contractParties, examTypes, departments } */
  options: () => requestFresh('/receipts/options'),
  /** GET /api/receipts/lookup?code= — a 13-digit serial (barcode) or an RC- number */
  lookup: (code) => requestFresh(`/receipts/lookup${qs({ code })}`),
};

/* --- المرحلة 1.35: the price list — Backend/routes/priceList.js ---------- */

export const priceListApi = {
  /** The editor's tree: { tree, leaves, units, maxDepth, canEdit, policy, templates } */
  get: () => requestFresh('/price-list'),
  /** The priced leaves only, for a dropdown: ?for=desk|nursing -> { leaves, units } */
  leaves: (forWho) => requestFresh(`/price-list${qs({ flat: 1, for: forWho })}`),
  create: (payload) => request('/price-list', { method: 'POST', body: payload }),
  update: (id, payload) => request(`/price-list/${id}`, { method: 'PUT', body: payload }),
  /** One level's order: { parentId, ids } */
  order: (parentId, ids) => request('/price-list/order', { method: 'PUT', body: { parentId, ids } }),
  hide: (id) => request(`/price-list/${id}`, { method: 'DELETE' }),
  /** Fill an empty list from a ready one: { key } -> { count, tree } */
  template: (key) => request('/price-list/template', { method: 'POST', body: { key } }),
  /** The named discounts: { discounts: [{ id, name, kind, value, target, itemIds, note, active }], canEdit } */
  discounts: () => requestFresh('/price-list/discounts'),
  createDiscount: (payload) => request('/price-list/discounts', { method: 'POST', body: payload }),
  updateDiscount: (id, payload) => request(`/price-list/discounts/${id}`, { method: 'PUT', body: payload }),
  closeDiscount: (id) => request(`/price-list/discounts/${id}`, { method: 'DELETE' }),
  /** Every voucher that used one, with the desk's reason: ?from&to -> { from, to, rows, totals } */
  discountUsage: (params = {}) => requestFresh(`/price-list/discounts/usage${qs(params)}`),
};

/* --- المرحلة 1.35: the quick reception — Backend/routes/quickDesk.js ------ */

export const quickDeskApi = {
  /** { leaves, units, discounts, policy: { quickReception, deferred }, signer, nextNumber, country, now } */
  options: () => requestFresh('/quick-desk/options'),
  /** One whole identifier -> { files: [{ id, fullName, isMinor, guardianName, mobileTail, lastReceipt }] } */
  find: (q) => requestFresh(`/quick-desk/find${qs({ q })}`),
  /**
   * { patient: { id } | { fullName, identifier: { kind, value }, isMinor, guardianName, guardianRelation, extra },
   *   payerName, payerRelation, lines: [{ priceItemId, quantity, discountId?, discountReason? }],
   *   discount?: { discountId, reason }, receiptType, paidAmount }
   * -> 201 { id, receipt, patient, words, major, minor, boxes }
   */
  issue: (payload) => request('/quick-desk/issue', { method: 'POST', body: payload }),
  /** The soft copy: { id, url, qrSvg, whatsappUrl, expiresAt, hasMobile } */
  link: (receiptId) => request(`/receipts/${receiptId}/link`, { method: 'POST', body: {} }),
  revokeLink: (linkId) => request(`/receipts/links/${linkId}`, { method: 'DELETE' }),
};

/* --- المرحلة 1.35: the child file — Backend/routes/childFile.js ---------- */

export const childFileApi = {
  /** { patient, profile, growth: [{ at, ageMonths, weight, length, head, comment, by }], percentiles, notes, stays, can, policy } */
  get: (patientId) => requestFresh(`/patients/${patientId}/child`),
  /** { profile, guardianName, guardianRelation, guardianPhone, guardianNationalId, motherName, address } -> the file */
  save: (patientId, payload) => request(`/patients/${patientId}/child`, { method: 'PUT', body: payload }),
  /** One row of the blue sheet: { at, weightKg, lengthCm, headCm, comment } -> 201 the file */
  addGrowth: (patientId, payload) => request(`/patients/${patientId}/child/growth`, { method: 'POST', body: payload }),
  /** A mistaken row, by its time -> the file */
  removeGrowth: (patientId, at) => request(`/patients/${patientId}/child/growth/${encodeURIComponent(at)}`, { method: 'DELETE' }),
  /** The notes page writes as every note does. */
  addNote: (patientId, text) => request(`/patients/${patientId}/notes`, { method: 'POST', body: { kind: 'note', text } }),
};

/* --- المرحلة 1.35: the stay and its booklet — Backend/routes/stays.js ----- */

export const staysApi = {
  /** { units, nursingItems, doctors, staff, dischargeKinds, policy, priceList } */
  options: () => requestFresh('/stays/options'),
  /** { stays: [{ ..., patient, totals }], policy } */
  list: (params = {}) => requestFresh(`/stays${qs(params)}`),
  /** { stay, patient, charges, payments, totals, accrual, booklet, policy, can } */
  get: (id) => requestFresh(`/stays/${id}`),
  /** { hospitalPatientId, unitItemId, admittedAt, attendingDoctorId, diagnosis, bookedAt, responsibleUserId } */
  admit: (payload) => request('/stays', { method: 'POST', body: payload }),
  update: (id, payload) => request(`/stays/${id}`, { method: 'PUT', body: payload }),
  /** A booklet page: { kind: vitals|medication|note|order|visit, payload, text } */
  record: (id, payload) => request(`/stays/${id}/records`, { method: 'POST', body: payload }),
  /** { priceItemId, quantity, occurredAt } or, by accounts, { label, categoryName, amount } */
  charge: (id, payload) => request(`/stays/${id}/charges`, { method: 'POST', body: payload }),
  accrue: (id) => request(`/stays/${id}/accrue`, { method: 'POST', body: {} }),
  review: (chargeId) => request(`/stays/charges/${chargeId}/review`, { method: 'PUT', body: {} }),
  voidCharge: (chargeId, reason) => request(`/stays/charges/${chargeId}/void`, { method: 'POST', body: { reason } }),
  /** { amount, paymentMethod } -> { receipt, words, ...the stay } */
  pay: (id, payload) => request(`/stays/${id}/payments`, { method: 'POST', body: payload }),
  clear: (id, reason) => request(`/stays/${id}/clear`, { method: 'POST', body: { reason } }),
  discharge: (id, kind) => request(`/stays/${id}/discharge`, { method: 'POST', body: { kind } }),
};

/** A receipt's soft copy, opened from WhatsApp with no account (auth: false, as the visit page). */
export const publicReceiptApi = {
  get: (token) => request(`/public/receipts/${encodeURIComponent(token)}`, { auth: false }),
};

/* --- contract parties — Backend/routes/contractParties.js --------------- */

export const contractPartiesApi = {
  list: (all = false) => requestFresh(`/contract-parties${all ? '?all=1' : ''}`),
  create: (payload) => request('/contract-parties', { method: 'POST', body: payload }),
  update: (id, payload) => request(`/contract-parties/${id}`, { method: 'PUT', body: payload }),
};

/* --- the clinics manager's additions — Backend/routes/operations.js ---- */

export const clinicsAdminApi = {
  list: () => requestFresh('/operations/clinics'),
  create: (payload) => request('/operations/clinics', { method: 'POST', body: payload }),
  update: (id, payload) => request(`/operations/clinics/${id}`, { method: 'PUT', body: payload }),
  updateDoctor: (doctorId, payload) =>
    request(`/operations/doctors/${doctorId}`, { method: 'PUT', body: payload }),
  closeDay: (payload) => request('/operations/closed-days', { method: 'POST', body: payload }),
  /** PUT /api/operations/notices/:id/status — contacted/confirmed or unreachable */
  setNoticeStatus: (id, status) =>
    request(`/operations/notices/${id}/status`, { method: 'PUT', body: { status } }),
};

/* --- the secretary's batch AI — Backend/routes/ai.js ------------------- */

export const aiBatchApi = {
  /** `premium`: the smartest model (needs the account's premium grant), else the fastest. */
  start: (bookingIds, { premium = false } = {}) =>
    request('/ai/batch-summaries', { method: 'POST', body: { bookingIds, premium: Boolean(premium) } }),
  status: () => requestFresh('/ai/batch-summaries/status'),
  summaries: (params = {}) => requestFresh(`/ai/summaries${qs(params)}`),
};

/* --- the Super Admin inside hospitals — Backend/routes/institutions.js - */

export const platformHospitalApi = {
  /** GET /api/institutions/:id/members — any hospital's roster and quota */
  members: (institutionId) => requestFresh(`/institutions/${institutionId}/members`),
  /** POST /api/institutions/:id/members — create staff directly, no quota */
  createMember: (institutionId, payload) =>
    request(`/institutions/${institutionId}/members`, { method: 'POST', body: payload }),
};

export const api = {
  auth: authApi,
  categories: categoryApi,
  doctors: doctorApi,
  clinics: clinicApi,
  bookings: bookingApi,
  prescriptions: prescriptionApi,
  prescriptionTemplates: prescriptionTemplateApi,
  institutions: institutionApi,
  services: serviceApi,
  medicalRecords: medicalRecordApi,
  photography: photographyApi,
  googleAccounts: googleAccountApi,
  sync: syncApi,
  autocomplete: autocompleteApi,
  team: teamApi,
  topics: topicApi,
  admin: adminApi,
  ai: aiApi,
  patients: patientsApi,
  requests: requestsApi,
  contracts: contractsApi,
  accounts: accountsApi,
  lab: labApi,
  radiology: radiologyApi,
  pharmacy: pharmacyApi,
  rooms: roomsApi,
  nursing: nursingApi,
  receipts: receiptsApi,
  clinicsAdmin: clinicsAdminApi,
  aiBatch: aiBatchApi,
  platformHospital: platformHospitalApi,
};

export default api;

/* --- the help assistant and support — Backend/routes/help.js, support.js - */

export const helpApi = {
  /** GET /api/help/capabilities -> { available, canUsePremium, premiumDailyLimit, usage, … } */
  capabilities: () => requestFresh('/help/capabilities'),
};

export const supportApi = {
  /** GET /api/support/unread -> { count, support } — the launcher's dot */
  unread: () => requestFresh('/support/unread'),
  /** GET /api/support/tickets?status=&mine=1 */
  tickets: (params = {}) => requestFresh(`/support/tickets${qs(params)}`),
  /** GET /api/support/tickets/:id -> { ticket, messages } */
  ticket: (id) => requestFresh(`/support/tickets/${id}`),
  /** POST /api/support/tickets { subject, body, pagePath, image, isScreenshot } */
  create: (payload) => request('/support/tickets', { method: 'POST', body: payload }),
  /** POST /api/support/tickets/:id/messages { body, image, isScreenshot } */
  reply: (id, payload) => request(`/support/tickets/${id}/messages`, { method: 'POST', body: payload }),
  /** PUT /api/support/tickets/:id/status { status } */
  setStatus: (id, status) => request(`/support/tickets/${id}/status`, { method: 'PUT', body: { status } }),
  /** The screenshot's bytes, with the session's token. */
  imageBlob: (messageId) => fetchBlob(`/support/messages/${messageId}/image`),
};

/* --- the central control (phase 0) — Backend/routes/control.js ----------- */

export const controlApi = {
  /** GET /api/control/catalog -> { states, groups, controls, services, upcoming, never, census, roles } */
  catalog: () => request('/control/catalog'),
  /** GET /api/control/plans -> { plans: [{ key, name, states, limits, policies, roles, addons, institutions }] } */
  plans: () => requestFresh('/control/plans'),
  /** PUT /api/control/plans/:key { states?, limits?, policies?, roles?, name?, description?, color?, minUsers?, reason } */
  savePlan: (key, payload) => request(`/control/plans/${encodeURIComponent(key)}`, { method: 'PUT', body: payload }),
  /** GET /api/control/institutions -> { institutions, plans } */
  institutions: () => requestFresh('/control/institutions'),
  /** GET /api/control/institutions/:id -> { institution, plan, controls, roles, exceptions, usage, addons, audit } */
  board: (id) => requestFresh(`/control/institutions/${id}`),
  /** PUT /api/control/institutions/:id/plan { planKey, reason } */
  setPlan: (id, planKey, reason) => request(`/control/institutions/${id}/plan`, { method: 'PUT', body: { planKey, reason } }),
  /** POST /api/control/institutions/:id/preview { controlId } -> { work, blocking, services, patientData } */
  preview: (id, controlId) => request(`/control/institutions/${id}/preview`, { method: 'POST', body: { controlId } }),
  /** PUT /api/control/institutions/:id/controls/:controlId { state, limits, policy, overage, expiresAt, revertTo, drain, note, reason } */
  setControl: (id, controlId, payload) =>
    request(`/control/institutions/${id}/controls/${encodeURIComponent(controlId)}`, { method: 'PUT', body: payload }),
  /** DELETE /api/control/institutions/:id/controls/:controlId { reason } — back to the package */
  clearControl: (id, controlId, reason) =>
    request(`/control/institutions/${id}/controls/${encodeURIComponent(controlId)}`, { method: 'DELETE', body: { reason } }),
  /** POST /api/control/institutions/:id/addons { group, trialDays?, reason } */
  addAddon: (id, payload) => request(`/control/institutions/${id}/addons`, { method: 'POST', body: payload }),
  /** GET /api/control/institutions/:id/audit?before= -> { audit } */
  audit: (id, before) => requestFresh(`/control/institutions/${id}/audit${qs({ before })}`),
};

/* ========================================================================== */
/* المرحلة 1.1 — the access log, exports, the platform, the API keys            */
/* ========================================================================== */

export const auditApi = {
  /** GET /api/audit?from&to&actorId&action&category&patientRef&q&page -> { rows, total, counts, last24, actors, labels } */
  list: (params = {}) => requestFresh(`/audit${qs(params)}`),
  /** GET /api/audit/alerts -> { threshold, alerts } */
  alerts: () => requestFresh('/audit/alerts'),
  /** GET /api/audit/export?format=xlsx|csv&… — the filtered log as a file. */
  download: (params = {}) => fetchBlob(`/audit/export${qs(params)}`),
};

export const exportApi = {
  /**
   * POST /api/exports { title, source, format, columns: [{ key, label, mask }], rows }
   *   -> { blob, rows, masked }
   */
  table: (payload) => postBlob('/exports', payload),
};

export const platformApi = {
  policies: () => requestFresh('/platform/policies'),
  setPolicy: (controlId, policy, reason) => request(`/platform/policies/${controlId}`, { method: 'PUT', body: { policy, reason } }),
  resetPolicy: (controlId) => request(`/platform/policies/${controlId}`, { method: 'DELETE' }),
  consent: () => requestFresh('/platform/consent'),
  publishConsent: (payload) => request('/platform/consent', { method: 'POST', body: payload }),
  messages: (status) => requestFresh(`/platform/messages${qs({ status })}`),
  team: () => requestFresh('/platform/team'),
  addMember: (payload) => request('/platform/team', { method: 'POST', body: payload }),
  updateMember: (userId, payload) => request(`/platform/team/${userId}`, { method: 'PUT', body: payload }),
  me: () => requestFresh('/platform/me'),
};

/* ── المرحلة 1.2 — the patient's health profile, readings and the bell ───── */

export const healthProfileApi = {
  get: () => requestFresh('/health-profile'),
  save: (step, fields) => request('/health-profile', { method: 'PUT', body: { step, ...fields } }),
  skip: (step) => request(`/health-profile/steps/${step}/skip`, { method: 'POST' }),
  resume: (step) => request(`/health-profile/steps/${step}/resume`, { method: 'POST' }),
  addAllergy: (payload) => request('/health-profile/allergies', { method: 'POST', body: payload }),
  updateAllergy: (id, payload) => request(`/health-profile/allergies/${id}`, { method: 'PUT', body: payload }),
  removeAllergy: (id) => request(`/health-profile/allergies/${id}`, { method: 'DELETE' }),
  saveCard: (payload) => request('/health-profile/insurance-card', { method: 'PUT', body: payload }),
};

export const observationApi = {
  list: (params = {}) => requestFresh(`/observations${qs(params)}`),
  add: (payload) => request('/observations', { method: 'POST', body: payload }),
};

export const notificationApi = {
  list: (params = {}) => requestFresh(`/notifications${qs(params)}`),
  ring: (payload) => request('/notifications', { method: 'POST', body: payload }),
  import: (items) => request('/notifications/import', { method: 'POST', body: { items } }),
  read: (id) => request(`/notifications/${id}/read`, { method: 'PUT' }),
  readAll: () => request('/notifications/read-all', { method: 'PUT' }),
  remove: (id) => request(`/notifications/${id}`, { method: 'DELETE' }),
};

/** A patient file's allergies, «من المريض», and its readings, at the desks. */
export const patientHealthApi = {
  allergies: (fileId) => requestFresh(`/patients/${fileId}/allergies`),
  addAllergy: (fileId, payload) => request(`/patients/${fileId}/allergies`, { method: 'POST', body: payload }),
  fromPatient: (fileId) => requestFresh(`/patients/${fileId}/from-patient`),
  accept: (fileId, payload) => request(`/patients/${fileId}/from-patient/accept`, { method: 'POST', body: payload }),
  observations: (fileId, params = {}) => requestFresh(`/patients/${fileId}/observations${qs(params)}`),
  approveObservation: (fileId, observationId) => request(`/patients/${fileId}/observations/${observationId}/approve`, { method: 'PUT' }),
  /** The editor's view of a patient's allergies before a prescription is saved. */
  forPrescription: (params = {}) => requestFresh(`/prescriptions/allergies${qs(params)}`),
};

export const developerApi = {
  clients: () => requestFresh('/developer/clients'),
  create: (payload) => request('/developer/clients', { method: 'POST', body: payload }),
  revoke: (id) => request(`/developer/clients/${id}`, { method: 'DELETE' }),
  docs: () => request('/v1/openapi.json', { auth: false }),
};
